import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import ReactMarkdown from "react-markdown";
import { Sparkles, ArrowLeft, Calendar, FileDown, Lock } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

function BriefingHeader({ b }) {
  const dateStr = b.published_at || b.generated_at;
  return (
    <div className="border-b border-slate-200 pb-5 mb-6">
      <div className="text-xs text-purple-700 font-semibold tracking-widest uppercase flex items-center gap-1.5">
        <Sparkles size={12} />
        {b.briefing_type === "simplified" ? "Report Sintetico" : "Report Tecnico"}
      </div>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
        Report Mercati Energetici
      </h1>
      <div className="mt-3 text-sm text-slate-600 flex items-center gap-2">
        <Calendar size={14} className="text-slate-400" />
        <span>
          Aggiornamento del <b className="text-slate-900">{fmtDate(dateStr)}</b>
        </span>
      </div>
      <div className="mt-1 text-xs text-slate-500 italic">
        A cura del Desk Analisi Mercati Energetici — Adybo S.r.l. - Via Aicardo, 2 - 20141 Milano - mail: direzione@adybo.it
      </div>
    </div>
  );
}

async function downloadBriefingPdf(b, setDl) {
  setDl(true);
  try {
    const resp = await api.get(`/briefings/${b.id}/pdf`, { responseType: "blob" });
    const blob = new Blob([resp.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const dateStr = (b.published_at || b.generated_at || "").slice(0, 10);
    a.download = `Adybo_Report_Mercati_${dateStr}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    toast.error("Errore generazione PDF");
  } finally {
    setDl(false);
  }
}

export default function Briefings() {
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "editor";
  const disabled = !isStaff && user?.market_reports_enabled === false;
  const [items, setItems] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const { id } = useParams();

  useEffect(() => {
    if (disabled) return;
    (async () => {
      const { data } = await api.get("/briefings");
      setItems(data);
    })();
  }, [disabled]);

  if (disabled) {
    return (
      <div className="space-y-6" data-testid="market-reports-disabled">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <Sparkles className="text-slate-400" size={26} /> Report Mercati Energetici
          </h1>
        </div>
        <div className="card-elevated p-8 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
            <Lock size={22} className="text-slate-400" />
          </div>
          <div className="font-display text-lg font-semibold text-slate-800">Modulo non abilitato</div>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            I Report Mercati Energetici non sono attivi per la tua azienda. Contatta il tuo referente Adybo per abilitarli.
          </p>
        </div>
      </div>
    );
  }

  if (id) {
    const b = items.find((x) => x.id === id);
    if (!b) return <div className="text-slate-500">Caricamento…</div>;
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <Link to="/briefing" className="text-sm text-blue-600 flex items-center gap-1">
            <ArrowLeft size={14} /> Tutti i report
          </Link>
          <button
            onClick={() => downloadBriefingPdf(b, setDownloading)}
            disabled={downloading}
            className="text-sm brand-gradient text-white rounded-lg px-4 py-2 font-semibold flex items-center gap-2 shadow disabled:opacity-40"
            data-testid="briefing-detail-download-pdf"
          >
            <FileDown size={15} /> {downloading ? "Preparazione…" : "Stampa PDF"}
          </button>
        </div>
        <div className="card-elevated p-8">
          <BriefingHeader b={b} />
          <div className="prose prose-slate max-w-none [&_h2]:font-display [&_h2]:text-blue-700 [&_h2]:text-lg [&_h2]:mt-5 [&_h2]:mb-1">
            <ReactMarkdown>{b.content_markdown || ""}</ReactMarkdown>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <Sparkles className="text-purple-600" size={26} /> Report Mercati Energetici
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          A cura del Desk Analisi Mercati Energetici — Adybo S.r.l. - Via Aicardo, 2 - 20141 Milano - mail: direzione@adybo.it
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((b) => (
          <div
            key={b.id}
            className="card-elevated p-6 group relative"
            data-testid={`briefing-card-${b.id}`}
          >
            <Link to={`/briefing/${b.id}`} className="block pr-24">
              <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                <span className="badge-neutral !bg-purple-50 !text-purple-700 flex items-center gap-1">
                  <Sparkles size={10} />
                  {b.briefing_type === "simplified" ? "Sintetico" : "Tecnico"}
                </span>
                <span>Aggiornato al {fmtDate(b.published_at || b.generated_at)}</span>
              </div>
              <h3 className="mt-2 font-display font-bold text-lg text-slate-900 group-hover:text-blue-600">
                Report Mercati Energetici
              </h3>
              <div className="text-xs text-slate-500 italic mt-0.5">
                A cura del Desk Analisi Mercati Energetici — Adybo S.r.l.
              </div>
              <p className="text-sm text-slate-600 mt-3 line-clamp-3">
                {(b.content_markdown || "").replace(/[#*]/g, "").slice(0, 220)}
              </p>
            </Link>
            <button
              onClick={(e) => {
                e.preventDefault();
                downloadBriefingPdf(b, setDownloading);
              }}
              disabled={downloading}
              className="absolute top-4 right-4 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1.5 font-semibold flex items-center gap-1.5 disabled:opacity-40"
              title="Stampa questo report in PDF"
              data-testid={`briefing-card-download-${b.id}`}
            >
              <FileDown size={12} /> PDF
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-sm text-slate-500">Nessun report disponibile.</div>
        )}
      </div>
    </div>
  );
}
